// Super_admin-only top-level page for the templated emails we send publishers
// (CTV-784). Lives at /admin/email-templates.
//
// Nothing is sent from here. Compose fills a template's merge fields from an
// organization and its stations, lets the admin tweak the result, and copies
// subject/body to the clipboard; "Mark as Sent" snapshots what was copied so
// "what did we tell KIMT?" is answerable later. Templates are seeded in the
// API and can be edited + saved (an override), or reset to the seed.
//
// Auth mirrors WaymarkCredentialsPage:
//   signed out → /admin; signed in but not super_admin → /admin; else render.
//
// Chrome mirrors PublisherOnboardEntry: when this session has visited a
// station dashboard, the page renders inside that station's sidebar (navigation
// chrome only; the page itself is org-agnostic). Cold direct visits get the
// standalone chrome.

import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { rememberedAdminStation } from '../../components/self-service/admin/lastAdminStation';
import PublisherAdminLayout from '../../components/self-service/admin/PublisherAdminLayout';
import { fetchPublisherConfig } from '../../data/publisherConfigs';
import type { PublisherConfig } from '../../types/selfService';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Copy,
  History,
  Loader2,
  Mail,
  PenLine,
  RotateCcw,
  Send,
} from 'lucide-react';
import { apiClient } from '../../lib/apiClient';
import { useAuth } from '../../components/self-service/auth/useAuth';

export interface EmailTemplateView {
  key: string;
  name: string;
  description: string;
  subject: string;
  body: string;
  isCustomized: boolean;
  updatedAt: string | null;
}

export interface OrganizationWithStations {
  id: string;
  name: string;
  stations: Array<{ id: string; name: string; slug: string }>;
}

interface MergeField {
  key: string;
  description: string;
}

interface RenderedTemplate {
  subject: string;
  body: string;
  unresolved: string[];
}

export interface EmailTemplateSendRow {
  id: string;
  templateKey: string;
  templateName: string;
  organizationId: string;
  organizationName: string;
  stationIds: string[];
  stationNames: string[];
  recipientNames: string;
  subject: string;
  body: string;
  sentBy: string | null;
  createdAt: string;
}

type Tab = 'compose' | 'templates' | 'history';

const TABS: Array<{ id: Tab; label: string; icon: React.ElementType }> = [
  { id: 'compose', label: 'Compose', icon: Mail },
  { id: 'templates', label: 'Templates', icon: PenLine },
  { id: 'history', label: 'History', icon: History },
];

const inputClass =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400';
const primaryBtn =
  'inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed';
// Email body text renders in Outlook's default font so what you see here is
// what lands in the message. Aptos ships with Microsoft 365; Segoe UI is the
// closest fallback on Windows, system-ui elsewhere.
const emailBodyStyle: React.CSSProperties = {
  fontFamily: 'Aptos, "Aptos Display", "Segoe UI", system-ui, -apple-system, sans-serif',
  fontSize: '14px',
  lineHeight: 1.55,
};
const secondaryBtn =
  'inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export const EmailTemplatesPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const isSuperAdmin = isAuthenticated && user?.rawRole === 'super_admin';

  const [tab, setTab] = React.useState<Tab>('compose');
  const [templates, setTemplates] = React.useState<EmailTemplateView[] | null>(null);
  const [orgs, setOrgs] = React.useState<OrganizationWithStations[] | null>(null);
  const [mergeFields, setMergeFields] = React.useState<MergeField[]>([]);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoadError(null);
    try {
      const [t, o, m] = await Promise.all([
        apiClient.get<EmailTemplateView[]>('/admin/email-templates'),
        apiClient.get<OrganizationWithStations[]>('/admin/email-templates/organizations'),
        apiClient.get<MergeField[]>('/admin/email-templates/merge-fields'),
      ]);
      setTemplates(t);
      setOrgs(o);
      setMergeFields(m);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load email templates');
    }
  }, []);

  React.useEffect(() => {
    if (isSuperAdmin) void load();
  }, [isSuperAdmin, load]);

  if (!isAuthenticated || !user) {
    return <Navigate to="/admin" state={{ from: location.pathname + location.search }} replace />;
  }
  if (user.rawRole !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }

  const loading = templates === null || orgs === null;

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50'}>
      {/* Wide cap: the body editor is the point of the page, and at 5xl an
          email wraps to a squished column while the viewport sits empty. */}
      <div className={embedded ? 'max-w-7xl' : 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10 md:py-14'}>
        {!embedded && (
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Back to admin
          </Link>
        )}
        <div className="mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-[0.14em] mb-4">
            <Mail className="w-3 h-3" />
            Email Templates
          </div>
          <h1 className="text-[28px] md:text-[36px] font-semibold tracking-tight text-slate-900 leading-tight">
            Publisher emails, filled in and ready to paste
          </h1>
          <p className="mt-3 text-[15px] text-slate-500 leading-relaxed max-w-2xl">
            Pick a template and an organization, tweak the result if you need to, then copy it
            into Outlook. Nothing is sent from here. Mark it as sent to keep a record of exactly
            what went out.
          </p>
        </div>

        <div role="tablist" className="mb-6 flex gap-1 border-b border-slate-200">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.id
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {loadError && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadError}
          </div>
        )}

        {loading && !loadError && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && tab === 'compose' && (
          <ComposeTab templates={templates} orgs={orgs} />
        )}
        {!loading && tab === 'templates' && (
          <TemplatesTab
            templates={templates}
            mergeFields={mergeFields}
            onChanged={(next) =>
              setTemplates((prev) => (prev ?? []).map((t) => (t.key === next.key ? next : t)))
            }
          />
        )}
        {!loading && tab === 'history' && <HistoryTab orgs={orgs} />}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Compose
// ---------------------------------------------------------------------------

const ComposeTab: React.FC<{
  templates: EmailTemplateView[];
  orgs: OrganizationWithStations[];
}> = ({ templates, orgs }) => {
  const [templateKey, setTemplateKey] = React.useState(templates[0]?.key ?? '');
  const [orgId, setOrgId] = React.useState('');
  const [stationIds, setStationIds] = React.useState<string[]>([]);
  const [recipientNames, setRecipientNames] = React.useState('');
  const [rendered, setRendered] = React.useState<RenderedTemplate | null>(null);
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');
  const [rendering, setRendering] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState<'subject' | 'body' | null>(null);
  const [copyWarning, setCopyWarning] = React.useState<string | null>(null);
  const [marking, setMarking] = React.useState(false);
  const [markedAt, setMarkedAt] = React.useState<string | null>(null);
  const [sendId, setSendId] = React.useState<string | null>(null);
  // Any edit to the inputs re-arms Mark as Sent (a new render = a new send).
  const clearSent = () => {
    setMarkedAt(null);
    setSendId(null);
  };

  const org = orgs.find((o) => o.id === orgId) ?? null;

  const selectOrg = (id: string) => {
    setOrgId(id);
    const next = orgs.find((o) => o.id === id);
    setStationIds(next ? next.stations.map((s) => s.id) : []);
    setRendered(null);
    clearSent();
  };

  const toggleStation = (id: string) => {
    setStationIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setRendered(null);
    clearSent();
  };

  const canRender = !!templateKey && !!orgId;

  const render = async () => {
    if (!canRender) return;
    setRendering(true);
    setError(null);
    setCopyWarning(null);
    clearSent();
    try {
      const out = await apiClient.post<RenderedTemplate>(
        `/admin/email-templates/${encodeURIComponent(templateKey)}/render`,
        { organizationId: orgId, stationIds, recipientNames },
      );
      setRendered(out);
      setSubject(out.subject);
      setBody(out.body);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fill in the template');
    } finally {
      setRendering(false);
    }
  };

  const copy = async (which: 'subject' | 'body') => {
    const text = which === 'subject' ? subject : body;
    const ok = await copyText(text);
    if (ok) {
      setCopied(which);
      window.setTimeout(() => setCopied(null), 1500);
      // Deliberately copy-then-warn, never block: the admin may be pasting
      // into a doc to finish there. But blank merge fields walking out the
      // door is the whole failure mode this page exists to prevent, so make
      // it loud at the moment it matters.
      const leftBlank = [...new Set([...text.matchAll(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g)].map((m) => m[0]))];
      setCopyWarning(
        leftBlank.length > 0
          ? `Copied, but it still contains blank fields: ${leftBlank.join(', ')}. Fill them in before this email goes out.`
          : null,
      );
    } else {
      setError('Could not copy to the clipboard. Select the text and copy it manually.');
    }
  };

  const markSent = async () => {
    if (!rendered || !org) return;
    setMarking(true);
    setError(null);
    try {
      const { id } = await apiClient.post<{ id: string }>('/admin/email-templates/sends', {
        templateKey,
        organizationId: orgId,
        stationIds,
        recipientNames,
        subject,
        body,
      });
      setSendId(id);
      setMarkedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record the send');
    } finally {
      setMarking(false);
    }
  };

  // Accidental click: delete the record we just wrote and re-arm the button.
  const undoSent = async () => {
    if (!sendId) return;
    setMarking(true);
    setError(null);
    try {
      await apiClient.delete(`/admin/email-templates/sends/${encodeURIComponent(sendId)}`);
      setSendId(null);
      clearSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to undo');
    } finally {
      setMarking(false);
    }
  };

  // Unresolved fields in the server render, still present in the edited text.
  const stillUnresolved = (rendered?.unresolved ?? []).filter(
    (f) => subject.includes(`{{${f}}}`) || body.includes(`{{${f}}}`),
  );

  return (
    <div className="grid gap-6 md:grid-cols-[320px_1fr]">
      <div className="space-y-4">
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
            Template
          </span>
          <select
            className={inputClass}
            value={templateKey}
            onChange={(e) => {
              setTemplateKey(e.target.value);
              setRendered(null);
              clearSent();
            }}
          >
            {templates.map((t) => (
              <option key={t.key} value={t.key}>
                {t.name}
              </option>
            ))}
          </select>
          {templates.find((t) => t.key === templateKey)?.description && (
            <span className="mt-1 block text-xs text-slate-500">
              {templates.find((t) => t.key === templateKey)!.description}
            </span>
          )}
        </label>

        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
            Organization
          </span>
          <select className={inputClass} value={orgId} onChange={(e) => selectOrg(e.target.value)}>
            <option value="">Select an organization…</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>

        {org && (
          <fieldset>
            <legend className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
              Stations
            </legend>
            {org.stations.length === 0 ? (
              <p className="text-sm text-slate-500">
                This organization has no self-service stations yet.
              </p>
            ) : (
              <div className="space-y-1">
                {org.stations.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm text-slate-800">
                    <input
                      type="checkbox"
                      checked={stationIds.includes(s.id)}
                      onChange={() => toggleStation(s.id)}
                    />
                    {s.name} <span className="text-slate-400">/{s.slug}</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        )}

        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
            Recipient Names
          </span>
          <input
            className={inputClass}
            placeholder="Comma-separated, e.g. Michelle, Denise"
            value={recipientNames}
            onChange={(e) => {
              setRecipientNames(e.target.value);
              setRendered(null);
              clearSent();
            }}
          />
          <span className="mt-1 block text-xs text-slate-500">
            Rendered as "Michelle and Denise" or "A, B, and C".
          </span>
        </label>

        <button type="button" className={primaryBtn} disabled={!canRender || rendering} onClick={render}>
          {rendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Fill In Template
        </button>
      </div>

      <div>
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!rendered && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-500">
            Choose a template and organization, then click Fill In Template.
          </div>
        )}

        {rendered && (
          <div className="space-y-4">
            {stillUnresolved.length > 0 && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Still blank: {stillUnresolved.map((f) => `{{${f}}}`).join(', ')}. Fill these
                  in before copying.
                </span>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Subject
                </span>
                <button type="button" className={secondaryBtn} onClick={() => void copy('subject')}>
                  {copied === 'subject' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === 'subject' ? 'Copied' : 'Copy Subject'}
                </button>
              </div>
              <input
                aria-label="Subject"
                className={inputClass}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {copyWarning && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-800"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{copyWarning}</span>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Body
                </span>
                <button type="button" className={secondaryBtn} onClick={() => void copy('body')}>
                  {copied === 'body' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === 'body' ? 'Copied' : 'Copy Body'}
                </button>
              </div>
              <textarea
                aria-label="Body"
                className={`${inputClass} min-h-[420px]`}
                style={emailBodyStyle}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className={primaryBtn}
                disabled={marking || !!markedAt}
                onClick={() => void markSent()}
              >
                {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {markedAt ? 'Marked as Sent' : 'Mark as Sent'}
              </button>
              {markedAt && (
                <>
                  <span className="text-sm text-slate-500">
                    Recorded at {markedAt}. It now appears under History.
                  </span>
                  <button
                    type="button"
                    className={secondaryBtn}
                    disabled={marking}
                    onClick={() => void undoSent()}
                  >
                    <RotateCcw className="w-4 h-4" />
                    Undo
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Templates (edit / save / reset)
// ---------------------------------------------------------------------------

const TemplatesTab: React.FC<{
  templates: EmailTemplateView[];
  mergeFields: MergeField[];
  onChanged: (next: EmailTemplateView) => void;
}> = ({ templates, mergeFields, onChanged }) => {
  const [key, setKey] = React.useState(templates[0]?.key ?? '');
  const current = templates.find((t) => t.key === key) ?? null;
  const [subject, setSubject] = React.useState(current?.subject ?? '');
  const [body, setBody] = React.useState(current?.body ?? '');
  const [saving, setSaving] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Re-seed the editor when the SELECTION changes. Save/reset update the
  // editor explicitly so their success message survives the template refresh.
  React.useEffect(() => {
    setSubject(current?.subject ?? '');
    setBody(current?.body ?? '');
    setMessage(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on selection only, by design
  }, [current?.key]);

  const dirty = !!current && (subject !== current.subject || body !== current.body);

  const save = async () => {
    if (!current) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const next = await apiClient.put<EmailTemplateView>(
        `/admin/email-templates/${encodeURIComponent(current.key)}`,
        { subject, body },
      );
      onChanged(next);
      setSubject(next.subject);
      setBody(next.body);
      setMessage('Saved. Compose now uses this version.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the template');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!current) return;
    if (!window.confirm('Reset this template to the built-in default? Your saved edits will be discarded.')) {
      return;
    }
    setResetting(true);
    setError(null);
    setMessage(null);
    try {
      const next = await apiClient.delete<EmailTemplateView>(
        `/admin/email-templates/${encodeURIComponent(current.key)}`,
      );
      onChanged(next);
      setSubject(next.subject);
      setBody(next.body);
      setMessage('Reset to the built-in default.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset the template');
    } finally {
      setResetting(false);
    }
  };

  if (!current) {
    return <div className="text-sm text-slate-500">No templates available.</div>;
  }

  return (
    <div className="grid gap-6 md:grid-cols-[320px_1fr]">
      <div className="space-y-4">
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
            Template
          </span>
          <select className={inputClass} value={key} onChange={(e) => setKey(e.target.value)}>
            {templates.map((t) => (
              <option key={t.key} value={t.key}>
                {t.name}
                {t.isCustomized ? ' (edited)' : ''}
              </option>
            ))}
          </select>
        </label>
        <p className="text-sm text-slate-500">{current.description}</p>
        <div className="text-xs text-slate-500">
          {current.isCustomized
            ? `Edited version saved${current.updatedAt ? ` ${new Date(current.updatedAt).toLocaleString()}` : ''}.`
            : 'Built-in default.'}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">
            Merge Fields
          </div>
          <ul className="space-y-1.5">
            {mergeFields.map((f) => (
              <li key={f.key} className="text-xs text-slate-600">
                <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px] text-slate-800">
                  {`{{${f.key}}}`}
                </code>{' '}
                {f.description}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {message && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {message}
          </div>
        )}
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
            Subject
          </span>
          <input
            aria-label="Template subject"
            className={inputClass}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
            Body
          </span>
          <textarea
            aria-label="Template body"
            className={`${inputClass} min-h-[480px]`}
            style={emailBodyStyle}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className={primaryBtn}
            disabled={!dirty || saving || !subject.trim() || !body.trim()}
            onClick={() => void save()}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save Template
          </button>
          <button
            type="button"
            className={secondaryBtn}
            disabled={!current.isCustomized || resetting}
            onClick={() => void reset()}
          >
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

const HistoryTab: React.FC<{ orgs: OrganizationWithStations[] }> = ({ orgs }) => {
  const [orgId, setOrgId] = React.useState('');
  const [rows, setRows] = React.useState<EmailTemplateSendRow[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    const qs = orgId ? `?organizationId=${encodeURIComponent(orgId)}` : '';
    apiClient
      .get<EmailTemplateSendRow[]>(`/admin/email-templates/sends${qs}`)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load history');
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const copyBody = async (row: EmailTemplateSendRow) => {
    if (await copyText(row.body)) {
      setCopiedId(row.id);
      window.setTimeout(() => setCopiedId(null), 1500);
    }
  };

  const remove = async (row: EmailTemplateSendRow) => {
    if (!window.confirm('Remove this record from the send history? This cannot be undone.')) return;
    setError(null);
    try {
      await apiClient.delete(`/admin/email-templates/sends/${encodeURIComponent(row.id)}`);
      setRows((prev) => (prev ?? []).filter((r) => r.id !== row.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove the record');
    }
  };

  return (
    <div className="space-y-4">
      <label className="block max-w-sm">
        <span className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
          Organization
        </span>
        <select className={inputClass} value={orgId} onChange={(e) => setOrgId(e.target.value)}>
          <option value="">All organizations</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </label>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {rows === null && !error && (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
        </div>
      )}
      {rows !== null && rows.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
          Nothing marked as sent yet.
        </div>
      )}
      {rows !== null && rows.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Sent</th>
                <th className="px-4 py-3 font-medium">Template</th>
                <th className="px-4 py-3 font-medium">Organization</th>
                <th className="px-4 py-3 font-medium">Stations</th>
                <th className="px-4 py-3 font-medium">To</th>
                <th className="px-4 py-3 font-medium sr-only">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">{r.templateName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.organizationName}</td>
                    <td className="px-4 py-3 text-slate-600">{r.stationNames.join(', ') || 'None'}</td>
                    <td className="px-4 py-3 text-slate-600">{r.recipientNames || 'Not recorded'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        className="text-sm font-medium text-slate-700 hover:text-slate-900 mr-3"
                        onClick={() => setOpen(open === r.id ? null : r.id)}
                      >
                        {open === r.id ? 'Hide' : 'View'}
                      </button>
                      <button
                        type="button"
                        className="text-sm font-medium text-slate-700 hover:text-slate-900"
                        onClick={() => void copyBody(r)}
                      >
                        {copiedId === r.id ? 'Copied' : 'Copy Body'}
                      </button>
                      <button
                        type="button"
                        className="ml-3 text-sm font-medium text-red-700 hover:text-red-900"
                        onClick={() => void remove(r)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                  {open === r.id && (
                    <tr>
                      <td colSpan={6} className="px-4 py-4 bg-slate-50">
                        <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                          Subject
                        </div>
                        <div className="text-sm text-slate-900 mb-3">{r.subject}</div>
                        <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                          Body
                        </div>
                        <pre className="whitespace-pre-wrap text-slate-800" style={emailBodyStyle}>
                          {r.body}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/**
 * /admin/email-templates entry. If this session has visited a station
 * dashboard, wrap the page in that station's sidebar so the rest of the admin
 * is one click away. The remembered station is NAVIGATION CHROME ONLY: the
 * page is org-agnostic and picks its own organization. Cold direct visits
 * render the standalone chrome. Same shape as PublisherOnboardEntry.
 */
const EmailTemplatesEntry: React.FC = () => {
  const { user } = useAuth();
  const rememberedSlug = React.useMemo(() => rememberedAdminStation(), []);
  const [shellConfig, setShellConfig] = React.useState<PublisherConfig | null | 'loading'>(
    rememberedSlug ? 'loading' : null,
  );
  React.useEffect(() => {
    if (!rememberedSlug) return;
    let cancelled = false;
    fetchPublisherConfig(rememberedSlug)
      .then((config) => {
        if (!cancelled) setShellConfig(config);
      })
      .catch(() => {
        if (!cancelled) setShellConfig(null);
      });
    return () => {
      cancelled = true;
    };
  }, [rememberedSlug]);

  if (shellConfig === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
      </div>
    );
  }
  if (!shellConfig) return <EmailTemplatesPage />;
  return (
    <PublisherAdminLayout
      context={{
        mode: 'single',
        config: shellConfig,
        slug: shellConfig.slug,
        user: { id: user?.id ?? '', email: user?.email ?? '', rawRole: user?.rawRole },
      }}
    >
      <EmailTemplatesPage embedded />
    </PublisherAdminLayout>
  );
};

export default EmailTemplatesEntry;
